<template>
    <div class="enps-rated-share-trend">
        <div class="enps-rated-share-trend__title">Доля оценённых по месяцам</div>

        <VSpinner v-if="isLoading"></VSpinner>
        <div class="enps-chart-placeholder" v-else-if="!hasChartData" style="height: 220px">Нет данных за период</div>
        <LineChart
            v-else
            :chart-data="chartData"
            :chart-options="chartOptions"
            :styles="{ height: '220px', position: 'relative' }"
        ></LineChart>
    </div>
</template>
<script>
// Components
import { Line } from 'vue-chartjs/legacy';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import http from '@/js/http';
import ruMonthNames from '@/js/ruMonthNames';
import { SERIES } from '@/js/enpsColors';

const LINE_COLOR = SERIES.ratedShare.main;

// Помесячная доля оценённых закрытых обращений (ratedShare из /trend): падение доли значит,
// что средней оценке за этот месяц нельзя доверять, какой бы она ни была — оценили слишком
// малую часть работы. Горизонт фиксированный (12 месяцев, без переключателя — график читается
// как «за последний год»), данные — тот же кэш, что у графика динамики средней.
@Component({
    components: {
        // Не под родным именем Line: оно совпадает с SVG-элементом <line>, и Vue 2 отказывается
        // регистрировать компонент с зарезервированным именем — график просто не рендерится.
        LineChart: Line,
        VSpinner,
    },
})
class EnpsRatedShareTrendChart extends Vue {
    // Отдел, выбранный администратором на странице ('' — все) — тот же срез, что у остальных
    // виджетов.
    @Prop({ type: String, default: '' })
    department;

    trendData = [];
    isLoading = false;
    loadSeq = 0;

    get hasChartData() {
        return this.trendData.some((row) => row.closedCount > 0);
    }

    get chartData() {
        return {
            labels: this.trendData.map((row) => this.monthLabel(row.month)),
            datasets: [
                {
                    label: 'Доля оценённых, %',
                    // Без клипа по chartArea: точки на краях шкалы (0 и 100) иначе срезаются
                    // наполовину границей области графика.
                    clip: false,
                    // Месяц без закрытых обращений — ratedShare === null с бэкенда; рисуем 0:
                    // тот же принцип «ведём от нуля», что у графика средней оценки.
                    data: this.trendData.map((row) => row.ratedShare ?? 0),
                    borderColor: LINE_COLOR,
                    backgroundColor: LINE_COLOR,
                    pointRadius: 4,
                    tension: 0.3,
                },
            ],
        };
    }

    get chartOptions() {
        const trend = this.trendData;

        return {
            responsive: true,
            maintainAspectRatio: false,
            // Место под точки на краях шкалы (clip: false у датасета): легенда скрыта, и без
            // отступа верхней точке некуда рисоваться.
            layout: { padding: { top: 8 } },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label(context) {
                            const row = trend[context.dataIndex] || {};

                            if (!row.closedCount) {
                                return 'Нет закрытых обращений';
                            }

                            return `${row.ratedShare}% · ${row.ratingsCount} оценённых из ${row.closedCount} закрытых`;
                        },
                    },
                },
            },
            scales: {
                y: { min: 0, max: 100 },
            },
        };
    }

    monthLabel(month) {
        const [year, monthNumber] = month.split('-');

        return `${ruMonthNames[Number(monthNumber) - 1]} ${year.slice(2)}`;
    }

    @Watch('department')
    onDepartmentChange() {
        this.load();
    }

    load() {
        // Без клиентского кэша (убран решением владельца продукта): от гонок при быстрой смене
        // отдела защищает счётчик — применяется только последний запрос.
        const seq = ++this.loadSeq;

        this.isLoading = true;

        http.get('/enps/dashboard/trend', { params: { months: 12, department: this.department } })
            .then(({ data }) => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.trendData = data;
            })
            .catch(() => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.trendData = [];
            })
            .finally(() => {
                if (seq === this.loadSeq) {
                    this.isLoading = false;
                }
            });
    }

    created() {
        this.load();
    }
}

export default EnpsRatedShareTrendChart;
</script>
<style lang="scss">
.enps-rated-share-trend {
    flex: 1;
    padding: 15px;
    border-radius: 8px;
    background-color: var(--color-white);
    box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);

    &__title {
        margin-bottom: 10px;
        font-weight: 600;
    }
}
</style>
